/**
 * Magento-parity PLP ATC:
 * - size chips update the hidden variant id and price
 * - no default size selection; ATC without a size → PDP
 * - label states: idle → adding… → added → idle
 * - show collection-header success banner when data-mg-state becomes "added"
 * - open cart drawer when data-mg-state becomes "added"
 */

import { StandardEvents } from '@shopify/events';

const ADDED_RESET_MS = 1600;
const ROW_HAS_SIZE_CLASS = 'mg-plp-add-to-bag--row-has-size';

/** @type {WeakSet<Element>} */
const observedGrids = new WeakSet();

/** @type {ResizeObserver | undefined} */
let gridResizeObserver;

let rowSyncScheduled = false;

function scheduleSyncPlpRowHeights() {
  if (rowSyncScheduled) return;
  rowSyncScheduled = true;
  requestAnimationFrame(() => {
    rowSyncScheduled = false;
    syncPlpRowHeights();
  });
}

/**
 * Reserve size-chip space only when another card in the same visual row has chips.
 * Rows with no size pickers hug the button so empty gap is not shown.
 * Also syncs Nosto carousels / SERP so ATC buttons align across slides and rows.
 */
function syncPlpRowHeights() {
  const grids = document.querySelectorAll(
    '.mg-product-grid .product-grid, .mg-product-grid .resource-list, .mg-nosto-carousel, [data-mg-nosto-track], #nosto-serp, .mg-nosto-serp-products'
  );

  grids.forEach((grid) => {
    // Nosto carousels: mg-nosto-carousel.js owns visible-page chip/ATC/height sync.
    // Skip before clearing so we don't wipe reserves the carousel just applied.
    const isNostoTrack =
      grid.classList.contains('mg-nosto-carousel') || grid.hasAttribute('data-mg-nosto-track');
    if (isNostoTrack) return;

    const slots = grid.querySelectorAll('mg-plp-add-to-bag');
    slots.forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;
      slot.classList.remove(ROW_HAS_SIZE_CLASS);
      slot.style.minHeight = '';
      const reserve = slot.querySelector('[data-mg-chip-reserve]');
      if (reserve) reserve.remove();
      const chips = slot.querySelector('.mg-plp-size-options');
      if (chips instanceof HTMLElement) chips.style.minHeight = '';
    });
    if (slots.length === 0) return;

    /** @type {Map<number, HTMLElement[]>} */
    const rows = new Map();

    slots.forEach((slot) => {
      if (!(slot instanceof HTMLElement)) return;
      const item =
        slot.closest(
          '.product-grid__item, .resource-list__item, .mg-nosto-product-card, .mg-nosto-serp-product, nosto-dynamic-card'
        ) || slot;
      const top = Math.round(item.getBoundingClientRect().top);
      const row = rows.get(top) || [];
      row.push(slot);
      rows.set(top, row);
    });

    rows.forEach((rowSlots) => {
      const chipRows = rowSlots
        .map((slot) => slot.querySelector('.mg-plp-size-options'))
        .filter((el) => el instanceof HTMLElement);
      if (chipRows.length === 0) return;

      const maxChipH = Math.ceil(
        Math.max(0, ...chipRows.map((el) => el.getBoundingClientRect().height))
      );
      chipRows.forEach((el) => {
        el.style.minHeight = `${maxChipH}px`;
      });

      rowSlots.forEach((slot) => {
        slot.classList.add(ROW_HAS_SIZE_CLASS);
        syncChipReserve(slot, maxChipH);
      });
    });
  });
}

/**
 * Cards without chips reserve the tallest chip-row height in the row.
 * @param {HTMLElement} slot
 * @param {number} chipH
 */
function syncChipReserve(slot, chipH) {
  let reserve = slot.querySelector('[data-mg-chip-reserve]');
  if (slot.querySelector('.mg-plp-size-options')) {
    if (reserve) reserve.remove();
    return;
  }
  if (chipH <= 0) {
    if (reserve) reserve.remove();
    return;
  }
  if (!(reserve instanceof HTMLElement)) {
    reserve = document.createElement('div');
    reserve.setAttribute('data-mg-chip-reserve', '');
    reserve.setAttribute('aria-hidden', 'true');
    slot.insertBefore(reserve, slot.firstChild);
  }
  if (!(reserve instanceof HTMLElement)) return;
  reserve.style.cssText = `display:block;flex:0 0 auto;width:100%;height:${chipH}px;min-height:${chipH}px;pointer-events:none;`;
}

/**
 * @param {Element | null} grid
 */
function observePlpGrid(grid) {
  if (!grid || observedGrids.has(grid)) return;
  observedGrids.add(grid);

  if (!gridResizeObserver) {
    gridResizeObserver = new ResizeObserver(() => scheduleSyncPlpRowHeights());
  }
  gridResizeObserver.observe(grid);
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', scheduleSyncPlpRowHeights);
  /** @type {Window & { __mgSyncPlpRowHeights?: () => void }} */
  (window).__mgSyncPlpRowHeights = scheduleSyncPlpRowHeights;
}

class MgPlpAddToBag extends HTMLElement {
  /** @type {number | undefined} */
  #resetTimer;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    observePlpGrid(
      this.closest(
        '.product-grid, .resource-list, .mg-nosto-carousel, [data-mg-nosto-track], #nosto-serp, .mg-nosto-serp-products'
      )
    );
    scheduleSyncPlpRowHeights();

    // Carousel owns its own height sync — ping it when DynamicCard ATC mounts late.
    if (this.closest('.mg-nosto-carousel, [data-mg-nosto-track], .mg-nosto-ready')) {
      window.dispatchEvent(new CustomEvent('mg:nosto-carousel-heights-sync'));
    }

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    // Capture on the card: a disabled pill does not receive the click, and the
    // card link behind it would otherwise navigate to the product page.
    const card = this.closest('product-card, .mg-pair-with-card, .mg-nosto-product-card');
    card?.addEventListener('click', this.#blockUnavailablePill, { signal, capture: true });

    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    form?.addEventListener('submit', this.#onSubmit, { signal, capture: true });

    const formComponent = this.#getFormComponent();
    formComponent?.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal,
    });
    formComponent?.addEventListener(StandardEvents.cartError, this.#onCartError, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
    scheduleSyncPlpRowHeights();
  }

  /** @returns {boolean} */
  #requiresSizeSelection() {
    return this.dataset.requiresSizeSelection === 'true';
  }

  /** @returns {HTMLButtonElement | HTMLOptionElement | null} */
  #getSelectedSize() {
    const selected = this.querySelector('[data-mg-size-option].is-selected');
    if (selected instanceof HTMLButtonElement) return selected;

    const select = this.querySelector('[data-mg-size-select]');
    if (select instanceof HTMLSelectElement && select.selectedOptions[0]) {
      return select.selectedOptions[0];
    }
    return null;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-mg-size-select]')) return;
    if (!this.contains(select)) return;

    const option = select.selectedOptions[0];
    if (!(option instanceof HTMLOptionElement)) return;

    const variantId = option.dataset.variantId || option.value;
    const available = option.dataset.available !== 'false';
    if (!variantId) return;

    const variantInput = this.querySelector('[data-mg-variant-id]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (option.dataset.variantPrice) this.#updateCardPrice(option.dataset.variantPrice);
    this.#updateCardImage(option.dataset.variantMediaId, option.dataset.variantImage);
    this.#updatePairImage(option.dataset.variantImage);

    const atc = this.querySelector('[data-mg-atc]');
    if (atc instanceof HTMLButtonElement) {
      atc.disabled = !available;
    }
  };

  /**
   * Update pair-with card image when size changes.
   * @param {string} [imageUrl]
   */
  #updatePairImage(imageUrl) {
    if (!imageUrl) return;
    const img = this.closest('.mg-pair-with-card')?.querySelector('.mg-pair-with-card__img');
    if (img instanceof HTMLImageElement) {
      img.src = imageUrl;
      img.srcset = '';
    }
  }

  /** Navigate to PDP when size is required but not chosen. */
  #redirectToProduct() {
    const url = this.dataset.productUrl;
    if (!url) return;
    this.#trackNostoProductOpen(url);
    window.location.assign(url);
  }

  /** @returns {HTMLElement | null} */
  #getFormComponent() {
    return this.querySelector('product-form-component');
  }

  /**
   * Only handle cart events that originated from this card's product form.
   * @param {Event} event
   */
  #isOwnCartEvent(event) {
    const formComponent = this.#getFormComponent();
    if (!formComponent) return false;
    return event.currentTarget === formComponent || event.target === formComponent;
  }

  /**
   * Out-of-stock pills must not select a size or follow the card link.
   * @param {Event} event
   */
  #blockUnavailablePill = (event) => {
    const sizeOption = this.#sizeOptionFromEvent(event);
    if (!sizeOption || !this.#isUnavailableSize(sizeOption)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * @param {Event} event
   * @returns {HTMLButtonElement | null}
   */
  #sizeOptionFromEvent(event) {
    /** @type {Element[]} */
    const nodes = [];
    if (event.target instanceof Element) nodes.push(event.target);
    if (event instanceof MouseEvent) {
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      if (hit instanceof Element && !nodes.includes(hit)) nodes.push(hit);
    }

    for (const node of nodes) {
      const sizeOption = node.closest('[data-mg-size-option]');
      if (sizeOption instanceof HTMLButtonElement && this.contains(sizeOption)) return sizeOption;
    }
    return null;
  }

  /**
   * @param {HTMLButtonElement} option
   * @returns {boolean}
   */
  #isUnavailableSize(option) {
    return option.disabled || option.dataset.available === 'false' || option.classList.contains('is-unavailable');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const sizeOption = this.#sizeOptionFromEvent(event);
    if (sizeOption) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.#isUnavailableSize(sizeOption)) this.#selectSize(sizeOption);
      return;
    }

    const target = /** @type {HTMLElement} */ (event.target);

    const atc = target.closest('[data-mg-atc]');
    if (atc instanceof HTMLButtonElement && this.contains(atc)) {
      // Prevent product-card navigation from swallowing the add click.
      event.stopPropagation();

      if (this.#requiresSizeSelection() && !this.#getSelectedSize()) {
        event.preventDefault();
        this.#redirectToProduct();
      }
    }
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    if (this.#requiresSizeSelection() && !this.#getSelectedSize()) {
      event.preventDefault();
      event.stopPropagation();
      this.#redirectToProduct();
      return;
    }

    // Pair-with size <select>: ensure hidden id matches current selection before submit.
    const select = this.querySelector('[data-mg-size-select]');
    if (select instanceof HTMLSelectElement && select.value) {
      const variantInput = this.querySelector('[data-mg-variant-id]');
      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = select.value;
      }
    }

    this.#setState('adding');
  };

  /** @param {HTMLButtonElement} option */
  #selectSize(option) {
    if (option.disabled || option.dataset.available === 'false') return;

    const variantId = option.dataset.variantId;
    const available = option.dataset.available === 'true';
    if (!variantId) return;

    this.querySelectorAll('[data-mg-size-option]').forEach((el) => {
      if (!(el instanceof HTMLButtonElement)) return;
      const selected = el === option;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    const variantInput = this.querySelector('[data-mg-variant-id]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (option.dataset.variantPrice) this.#updateCardPrice(option.dataset.variantPrice);
    this.#updateCardImage(option.dataset.variantMediaId, option.dataset.variantImage);
    this.#updatePairImage(option.dataset.variantImage);
    this.#updateBundlePriceLabel(option.dataset.bundlePriceLabel || '');
    this.#updateVariantBadge(option);

    const atc = this.querySelector('[data-mg-atc]');
    if (atc instanceof HTMLButtonElement) {
      atc.disabled = !available;
    }
  }

  /**
   * Move the card gallery to the selected size variant's image.
   * Nosto injects the card after load. The slideshow on that card is disabled,
   * so scrolling to the variant slide does not run. The visible <img> is updated
   * directly when the slide swap leaves the previous photo on screen.
   * @param {string} [mediaId] - Media id of the variant's first gallery image.
   * @param {string} [imageUrl] - URL of that same image.
   */
  #updateCardImage(mediaId, imageUrl) {
    const card = /** @type {(HTMLElement & { selectVariantMedia?: (mediaId: string) => void }) | null} */ (
      this.closest('product-card')
    );
    if (!card) return;

    if (mediaId && typeof card.selectVariantMedia === 'function') {
      card.selectVariantMedia(mediaId);
    }

    this.#swapVisibleCardImage(card, imageUrl);
  }

  /**
   * Point the gallery photo the shopper can see at the selected variant image.
   * @param {HTMLElement} card
   * @param {string} [imageUrl]
   */
  #swapVisibleCardImage(card, imageUrl) {
    if (!imageUrl) return;

    const file = imageUrl.split('?')[0].split('/').pop();
    if (!file) return;

    const imgs = [...card.querySelectorAll('.card-gallery img')].filter(
      (img) => img instanceof HTMLImageElement
    );
    const visible =
      imgs.find((img) => img.getBoundingClientRect().width > 0 && img.getBoundingClientRect().height > 0) ||
      imgs[0];
    if (!(visible instanceof HTMLImageElement)) return;

    const current = (visible.currentSrc || visible.getAttribute('src') || '').split('?')[0];
    if (current.endsWith(file)) return;

    visible.removeAttribute('srcset');
    visible.removeAttribute('sizes');
    visible.src = imageUrl;
  }

  /**
   * Update the card's price to match the selected size variant.
   * @param {string} price - Formatted price string.
   */
  #updateCardPrice(price) {
    const pairPrice = this.closest('.mg-pair-with-card')?.querySelector('[data-mg-pair-price]');
    if (pairPrice instanceof HTMLElement) {
      pairPrice.textContent = price;
      return;
    }

    const priceEl = this.closest('product-card')?.querySelector(
      "product-price [ref='priceContainer'] .price"
    );
    if (priceEl instanceof HTMLElement) priceEl.textContent = price;
  }

  /**
   * Show/hide the variant metafield label under the product title.
   * @param {string} label - Bundle price label for the selected variant.
   */
  #updateBundlePriceLabel(label) {
    const labelEl = this.closest('product-card')?.querySelector('[data-mg-bundle-price-label]');
    if (!(labelEl instanceof HTMLElement)) return;

    const text = label.trim();
    labelEl.textContent = text;
    labelEl.hidden = text === '';
  }

  /**
   * Show sale or travel badge for the selected variant (sale wins when both apply).
   * @param {HTMLButtonElement} option - Selected size option.
   */
  #updateVariantBadge(option) {
    const badge = this.closest('product-card')?.querySelector('[data-mg-variant-badge]');
    if (!(badge instanceof HTMLElement)) return;

    const enableSale = badge.dataset.enableSale === 'true';
    const enableTravel = badge.dataset.enableTravel === 'true';
    const onSale = option.dataset.onSale === 'true';
    const travel = (option.dataset.travelSize || '').trim();
    const labelEl = badge.querySelector('[data-mg-variant-badge-label]');

    let type = '';
    let label = '';

    if (enableSale && onSale) {
      type = 'sale';
      label = badge.dataset.saleLabel || 'sale.';
    } else if (enableTravel && travel) {
      type = 'travel';
      label = travel;
    }

    badge.classList.toggle('mg-product-badge--sale', type === 'sale');
    badge.classList.toggle('mg-product-badge--travel', type === 'travel');
    if (labelEl instanceof HTMLElement) labelEl.textContent = label;
    badge.hidden = type === '';
  }

  /**
   * @param {Event} event
   */
  #onCartLinesUpdate = (event) => {
    if (!this.#isOwnCartEvent(event)) return;

    const cartEvent = /** @type {{ action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} */ (
      event
    );

    if (cartEvent.action && cartEvent.action !== 'add') return;
    if (!(cartEvent.promise instanceof Promise)) return;

    cartEvent.promise
      .then((result) => {
        if (result?.detail?.didError) {
          this.#setState('idle');
          return;
        }

        this.#trackRecommendationAdd();
        this.#setState('added');
      })
      .catch(() => {
        this.#setState('idle');
      });
  };

  /**
   * Recommendation slots only. Search and autocomplete already call recordSearchAddToCart.
   * Product id, not variant id. Attribution key comes from the Nosto template when it sets
   * data-nosto-attribution-key; otherwise the placement id.
   */
  #trackRecommendationAdd() {
    const widget = this.#recommendationWidget();
    if (!widget) return;

    const productId = this.#recommendationProductId();
    const attributionKey = this.#recommendationAttributionKey(widget);
    if (!productId || !attributionKey) return;

    const nostojs = window.nostojs;
    if (typeof nostojs !== 'function') return;

    try {
      nostojs((api) => {
        if (typeof api?.recommendedProductAddedToCart !== 'function') return;
        api.recommendedProductAddedToCart(String(productId), attributionKey);
      });
    } catch {
      // Analytics must not block the cart.
    }
  }

  /**
   * Product open from a JavaScript redirect only. A normal product link is left alone.
   * Search and autocomplete use recordSearchClick. A recommendation slot submits a product view.
   * @param {string} url
   */
  #trackNostoProductOpen(url) {
    const productId = this.#recommendationProductId();
    if (!productId) return;

    const nostojs = window.nostojs;
    if (typeof nostojs !== 'function') return;

    const absoluteUrl = this.#absoluteProductUrl(url);
    const searchType = this.#searchSurface();
    const widget = searchType ? null : this.#recommendationWidget();
    const attributionKey = widget ? this.#recommendationAttributionKey(widget) : '';
    if (!searchType && !widget) return;
    if (searchType && !absoluteUrl) return;
    if (widget && !attributionKey) return;

    try {
      nostojs((api) => {
        if (searchType) {
          if (typeof api?.recordSearchClick !== 'function') return;
          api.recordSearchClick(searchType, { productId: String(productId), url: absoluteUrl });
          return;
        }

        if (typeof api?.createRecommendationRequest !== 'function') return;
        const request = api.createRecommendationRequest();
        if (typeof request?.setProducts !== 'function') return;
        const next = request.setProducts([{ product_id: String(productId) }], attributionKey);
        const pending = next && typeof next.load === 'function' ? next : request;
        if (typeof pending.load === 'function') pending.load();
      });
    } catch {
      // Analytics must not block navigation.
    }
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #absoluteProductUrl(url) {
    if (!url) return '';
    if (url.startsWith('/')) return new URL(url, window.location.origin).href;
    return url;
  }

  /** @returns {'serp' | 'autocomplete' | 'category' | null} */
  #searchSurface() {
    const chain = this.#hostChain();
    if (chain.some((el) => el.id === 'nosto-autocomplete')) return 'autocomplete';
    if (!chain.some((el) => el.id === 'nosto-serp')) return null;

    const template = document.getElementById('MainContent')?.getAttribute('data-template') || '';
    return template.startsWith('collection') ? 'category' : 'serp';
  }

  /** @returns {Element[]} */
  #hostChain() {
    /** @type {Element[]} */
    const chain = [];
    /** @type {Node | null} */
    let node = this;

    while (node) {
      if (node instanceof Element) chain.push(node);
      const root = node.getRootNode();
      if (root instanceof ShadowRoot) {
        node = root.host;
      } else if (node instanceof Element) {
        node = node.parentElement;
      } else {
        break;
      }
    }

    return chain;
  }

  /** @returns {Element | null} */
  #recommendationWidget() {
    const chain = this.#hostChain();
    if (chain.some((el) => el.id === 'nosto-serp' || el.id === 'nosto-autocomplete')) return null;
    return chain.find((el) => el.classList.contains('nosto_element')) || null;
  }

  /** @returns {string} */
  #recommendationProductId() {
    const formId = this.querySelector('product-form-component')?.getAttribute('data-product-id');
    if (formId) return formId;

    for (const el of this.#hostChain()) {
      const id = el.getAttribute('data-nosto-product-id') || el.getAttribute('data-product-id');
      if (id) return id;
    }

    return '';
  }

  /**
   * @param {Element} widget
   * @returns {string}
   */
  #recommendationAttributionKey(widget) {
    for (const el of this.#hostChain()) {
      const key = el.getAttribute('data-nosto-attribution-key') || el.getAttribute('data-attribution-key');
      if (key) return key;
    }

    return widget.id || '';
  }

  /**
   * @param {Event} event
   */
  #onCartError = (event) => {
    if (!this.#isOwnCartEvent(event)) return;
    this.#setState('idle');
  };

  /** Open theme cart drawer when ATC reaches the "added" state. */
  #openCartDrawer() {
    /** @type {HTMLElement & { open?: () => void; isOpen?: boolean } | null} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer || typeof drawer.open !== 'function' || drawer.isOpen) return;
    drawer.open();
  }

  /**
   * Magento PLP: success message between collection title and description.
   * @returns {boolean} whether the banner was shown
   */
  #showCollectionCartMessage() {
    const banner = document.getElementById('mg-plp-cart-message');
    if (!(banner instanceof HTMLElement)) return false;

    const textEl = banner.querySelector('.mg-collection-header__cart-message-text');
    if (!(textEl instanceof HTMLElement)) return false;

    let title = (this.dataset.productTitle || '').trim();
    if (!title) return false;

    title = title.toLowerCase();
    if (!title.endsWith('.')) title = `${title}.`;

    const template =
      banner.dataset.messageTemplate || 'You added [[product_title]] to your shopping cart.';
    textEl.textContent = template.replace('[[product_title]]', title);

    banner.hidden = false;

    return true;
  }

  /**
   * @param {'idle' | 'adding' | 'added'} state
   */
  #setState(state) {
    if (this.#resetTimer) {
      clearTimeout(this.#resetTimer);
      this.#resetTimer = undefined;
    }

    const button = this.querySelector('[data-mg-atc]');

    if (state === 'idle') {
      delete this.dataset.mgState;
      if (button instanceof HTMLButtonElement) {
        const selected = this.#getSelectedSize();
        // No selection yet: keep ATC enabled so click can redirect to PDP.
        if (this.#requiresSizeSelection() && !selected) {
          button.disabled = false;
        } else {
          const available = selected ? selected.dataset.available === 'true' : true;
          button.disabled = !available;
        }
        button.removeAttribute('aria-busy');
        this.#syncLabels(button, 'idle');
      }
      return;
    }

    this.dataset.mgState = state;

    if (button instanceof HTMLButtonElement) {
      if (state === 'adding') {
        queueMicrotask(() => {
          button.disabled = true;
        });
        button.setAttribute('aria-busy', 'true');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      this.#syncLabels(button, state);
    }

    if (state === 'added') {
      this.#showCollectionCartMessage();
      this.#openCartDrawer();
      this.#resetTimer = window.setTimeout(() => this.#setState('idle'), ADDED_RESET_MS);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {'idle' | 'adding' | 'added'} state
   */
  #syncLabels(button, state) {
    button.querySelectorAll('.mg-plp-add-to-bag__label').forEach((label) => {
      const isActive = label.classList.contains(`mg-plp-add-to-bag__label--${state}`);
      label.toggleAttribute('aria-hidden', !isActive);
    });
  }
}

if (!customElements.get('mg-plp-add-to-bag')) {
  customElements.define('mg-plp-add-to-bag', MgPlpAddToBag);
}

/** Nosto SERP injects DynamicCards async — re-sync chip/ATC row heights when markup arrives. */
if (typeof window !== 'undefined') {
  const observeNostoSerp = () => {
    const serp = document.querySelector('#nosto-serp');
    if (!serp || observedGrids.has(serp)) return;
    observePlpGrid(serp);
    new MutationObserver(() => scheduleSyncPlpRowHeights()).observe(serp, {
      childList: true,
      subtree: true,
    });
    scheduleSyncPlpRowHeights();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeNostoSerp, { once: true });
  } else {
    observeNostoSerp();
  }
  window.addEventListener('mg:plp-row-heights-sync', scheduleSyncPlpRowHeights);
}
